import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useT } from '@/contexts/I18nContext';
import useSWR from 'swr';
import { updateZone } from '../api/zones';
import type { ZoneType } from '../api/zones';
import { listBrands, type BrandDto } from '../api/brands';
import { useCurrentZone } from '../hooks/useCurrentZone';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useCurrentUser } from '../hooks/useCurrentUser';
import InlineEditableText from './common/InlineEditableText';
import InlineEditableSelect from './common/InlineEditableSelect';
import ScheduleDisplay from './working-hours/ScheduleDisplay';
import { extractApiError } from '../utils/apiError';

// ── Timezone list ─────────────────────────────────────────────────────────────

const TIMEZONES: string[] = (() => {
  try {
    return (Intl as never as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf('timeZone');
  } catch {
    return [
      'UTC',
      'Europe/Kaliningrad', 'Europe/Moscow', 'Europe/Samara',
      'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Krasnoyarsk',
      'Asia/Irkutsk', 'Asia/Yakutsk', 'Asia/Vladivostok',
      'Asia/Magadan', 'Asia/Kamchatka',
      'Europe/Minsk', 'Europe/Kiev',
      'Asia/Almaty', 'Asia/Tashkent',
    ];
  }
})();

// Pre-compute UTC offset labels once at module load (fast — ~400 Intl calls).
// Result: "Europe/Moscow" → "Europe Moscow (UTC+3)"
const _tzNow = new Date();
const TZ_LABEL = new Map<string, string>(
  TIMEZONES.map(tz => {
    try {
      const parts = new Intl.DateTimeFormat('en', {
        timeZone: tz,
        timeZoneName: 'shortOffset',
      }).formatToParts(_tzNow);
      const offset = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
      return [tz, `${tz.replace(/_/g, ' ')} (${offset})`] as const;
    } catch {
      return [tz, tz.replace(/_/g, ' ')] as const;
    }
  }),
);

// Zone type choices matching backend ZoneTypes
const ZONE_TYPES: ZoneType[] = [
  'RETAIL_BRANCH', 'COFFEESHOP', 'SHOP', 'CANTEEN',
  'BUILDING', 'FLOOR', 'HALLWAY', 'ROOM',
  'CLASSROOM', 'GYM', 'COWORKING',
];

// ── BranchGeneralSettings ─────────────────────────────────────────────────────

const BranchGeneralSettings: React.FC = () => {
  const { t } = useT();
  const { zoneId, currentZone, mutate: mutateZones } = useCurrentZone();
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;
  // Сеть и ссылка на страницу филиала — служебные поля (Алексей 08.10, #1324): список сетей —
  // это список наших клиентов, его не показываем; правят только staff.
  const isStaff = user?.is_staff === true || isSuperuser;
  const [nameError, setNameError] = useState<string | null>(null);
  const [tzError, setTzError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [brandError, setBrandError] = useState<string | null>(null);
  // Сети филиалов (Алексей 07.10, #1278): справочник общий, выбор — на филиале.
  const { data: brands = [] } = useSWR<BrandDto[]>('brands', async () => (await listBrands()).data);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  useEffect(() => { setNameError(null); setTzError(null); setArchiveError(null); setWebsiteError(null); setBrandError(null); }, [zoneId]);

  async function handleSaveName(nextName: string) {
    if (!zoneId) return;
    try {
      await updateZone(zoneId, { humanreadable_name: nextName });
      await mutateZones();
    } catch (err) {
      setNameError(extractApiError(err, t('error.savingName')));
      throw err;
    }
  }

  async function handleSaveAddress(nextAddress: string) {
    if (!zoneId) return;
    try {
      await updateZone(zoneId, { address: nextAddress.trim() });
      await mutateZones();
    } catch (err) {
      setAddressError(extractApiError(err, t('error.general')));
      throw err;
    }
  }

  async function handleSaveWebsite(next: string) {
    if (!zoneId) return;
    try {
      await updateZone(zoneId, { website_url: next.trim() });
      await mutateZones();
    } catch (err) {
      setWebsiteError(extractApiError(err, t('error.general')));
      throw err;
    }
  }

  async function handleSaveBrand(next: string) {
    if (!zoneId) return;
    setBrandError(null);
    try {
      await updateZone(zoneId, { brand: next ? Number(next) : null });
      await mutateZones();
    } catch (err) {
      setBrandError(extractApiError(err, t('error.general')));
    }
  }

  async function handleSaveTimezone(nextTz: string) {
    if (!zoneId) return;
    setTzError(null);
    try {
      await updateZone(zoneId, { timezone: nextTz });
      await mutateZones();
    } catch (err) {
      setTzError(extractApiError(err, t('error.savingTimezone')));
    }
  }

  async function handleSaveZoneType(nextType: string) {
    if (!zoneId) return;
    try {
      await updateZone(zoneId, { type: nextType as ZoneType });
      await mutateZones();
    } catch (err) {
      throw err;
    }
  }

  async function handleArchive() {
    if (!zoneId) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      await updateZone(zoneId, { is_archived: true });
      await mutateZones();
      setArchiveConfirm(false);
    } catch (err) {
      setArchiveError(extractApiError(err, t('general.archiveError')));
    } finally {
      setArchiving(false);
    }
  }

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  const currentTz = currentZone?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const brandName = currentZone?.brand != null ? brands.find(b => b.id === currentZone.brand)?.name : undefined;

  return (
    <Card variant="section">
      <CardContent className="space-y-5">
        <section>
          <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
            {t('general.branchName')}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <InlineEditableText
              value={currentZone?.humanreadable_name ?? ''}
              onSave={handleSaveName}
              onError={setNameError}
              disabled={!zoneId}
              ariaLabel={t('general.branchName')}
              fontSize="1rem"
              fontWeight={400}
            />
            {brandName && (
              <span className="rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-xs font-medium text-primary">
                {brandName}
              </span>
            )}
          </div>
          {nameError && (
            <div className="mt-2 max-w-md rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {nameError}
            </div>
          )}
        </section>

        <section>
          <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
            {t('general.address')}
          </div>
          <InlineEditableText
            value={currentZone?.address ?? ''}
            onSave={handleSaveAddress}
            onError={setAddressError}
            disabled={!zoneId}
            ariaLabel={t('general.address')}
            placeholder={t('general.addressPlaceholder')}
            fontSize="1rem"
            fontWeight={400}
          />
          {addressError && (
            <div className="mt-2 max-w-md rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {addressError}
            </div>
          )}
        </section>

        {isStaff && <section>
          <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
            {t('general.brand')}
          </div>
          <InlineEditableSelect
            value={currentZone?.brand != null ? String(currentZone.brand) : ''}
            options={['', ...brands.map(b => String(b.id))]}
            onSave={handleSaveBrand}
            getOptionLabel={id => (id ? (brands.find(b => String(b.id) === id)?.name ?? id) : t('general.brand.none'))}
            disabled={!zoneId}
            fontSize="1rem"
            fontWeight={400}
          />
          {brandError && (
            <div className="mt-2 max-w-md rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {brandError}
            </div>
          )}
        </section>}

        {(isStaff || currentZone?.website_url) && <section>
          <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
            {t('general.websiteUrl')}
          </div>
          <div className="flex items-center gap-2">
            <InlineEditableText
              value={currentZone?.website_url ?? ''}
              onSave={handleSaveWebsite}
              onError={setWebsiteError}
              disabled={!zoneId || !isStaff}
              ariaLabel={t('general.websiteUrl')}
              placeholder={t('general.websiteUrlPlaceholder')}
              fontSize="1rem"
              fontWeight={400}
            />
            {currentZone?.website_url && (
              <a href={currentZone.website_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:underline">
                ↗
              </a>
            )}
          </div>
          {websiteError && (
            <div className="mt-2 max-w-md rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {websiteError}
            </div>
          )}
        </section>}

        <section>
          <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
            {t('general.timezone')}
          </div>
          <InlineEditableSelect
            value={currentTz}
            options={TIMEZONES}
            onSave={handleSaveTimezone}
            getOptionLabel={tz => TZ_LABEL.get(tz) ?? tz.replace(/_/g, ' ')}
            groupBy={tz => tz.split('/')[0]}
            disabled={!zoneId}
            fontSize="1rem"
            fontWeight={400}
          />
          {tzError && (
            <div className="mt-2 max-w-md rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {tzError}
            </div>
          )}
        </section>

        {isSuperuser && (
          <section>
            <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
              {t('general.zoneType')}
            </div>
            <InlineEditableSelect
              value={currentZone?.type ?? 'ROOM'}
              options={ZONE_TYPES}
              onSave={handleSaveZoneType}
              getOptionLabel={type => t(`general.zoneType.${type}` as Parameters<typeof t>[0]) || type}
              disabled={!zoneId}
              fontSize="1rem"
              fontWeight={400}
            />
          </section>
        )}

        <ScheduleDisplay zoneId={zoneId} />


        {/* «Подключение функций» — во вкладке «Настройки функций → Подключённые функции» (Алексей 04.11 #4068). */}
        {isSuperuser && (
          <section>
            <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground text-destructive/70">
              {t('general.dangerZone')}
            </div>
            {!archiveConfirm ? (
              <button
                type="button"
                onClick={() => setArchiveConfirm(true)}
                className="rounded border border-destructive/40 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
              >
                {t('general.archive')}
              </button>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-destructive">{t('general.archiveConfirm')}</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={archiving}
                    onClick={handleArchive}
                    className="rounded bg-destructive px-3 py-1.5 text-sm text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 transition-colors"
                  >
                    {archiving ? t('general.archiving') : t('general.archiveConfirmBtn')}
                  </button>
                  <button
                    type="button"
                    disabled={archiving}
                    onClick={() => setArchiveConfirm(false)}
                    className="rounded border px-3 py-1.5 text-sm hover:bg-muted transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
                {archiveError && (
                  <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{archiveError}</div>
                )}
              </div>
            )}
          </section>
        )}
      </CardContent>
    </Card>
  );
};

export default BranchGeneralSettings;
